import React, { useState, useEffect, useMemo } from 'react';
import { spacing, borderRadius } from '../../constants/designTokens';
import { useTheme } from '../../contexts/ThemeContext';
import brandIcon from '../../assets/ai-image-ascii.png';

// Simulated code that gets "typed" out
const codeLines = [
  'const cx = 30, cy = 17.5;',
  'const dx = x - cx, dy = y - cy;',
  'const dist = Math.sqrt(dx*dx + dy*dy);',
  'const angle = Math.atan2(dy, dx);',
  '',
  'const wave1 = Math.sin(dist * 0.3 - t);',
  'const wave2 = Math.cos(angle * 4 + t);',
  'const spiral = Math.sin(angle * 6 - dist * 0.2);',
  '',
  'return (wave1 * wave2 + spiral) * density;'
];

const stages = [
  { text: 'Analyzing prompt', code: 'parse_input()' },
  { text: 'Processing patterns', code: 'extract_meaning()' },
  { text: 'Generating visuals', code: 'create_visuals()' },
  { text: 'Optimizing output', code: 'refine_ascii()' },
  { text: 'Finalizing', code: 'render_pattern()' }
];

// Characters for the morphing ASCII
const asciiChars = ' .:-=+*#%@';

/**
 * Get time-based greeting
 */
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return 'Good to see you';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  if (hour >= 17 && hour < 21) return 'Good evening';
  return 'Hello, night owl';
};

/**
 * GenerationSkeleton Component
 *
 * Chat-style AI generation interface that shows the conversation
 * between user prompt and AI response being generated.
 */
const GenerationSkeleton = ({ userPrompt = 'Generating pattern...' }) => {
  const { colors } = useTheme();
  const [currentStage, setCurrentStage] = useState(0);
  const [typedCode, setTypedCode] = useState('');
  const [currentLine, setCurrentLine] = useState(0);
  const [currentChar, setCurrentChar] = useState(0);
  const [asciiFrame, setAsciiFrame] = useState(0);

  // Progress synced with stages
  const progress = Math.min(((currentStage + 1) / stages.length) * 100, 100);

  // Stage progression
  useEffect(() => {
    const stageInterval = setInterval(() => {
      setCurrentStage((prev) => {
        if (prev >= stages.length - 1) return prev;
        return prev + 1;
      });
    }, 2200);
    return () => clearInterval(stageInterval);
  }, []);

  // Code typing effect
  useEffect(() => {
    const typeInterval = setInterval(() => {
      if (currentLine < codeLines.length) {
        const line = codeLines[currentLine];
        if (currentChar < line.length) {
          setTypedCode(prev => prev + line[currentChar]);
          setCurrentChar(prev => prev + 1);
        } else {
          setTypedCode(prev => prev + '\n');
          setCurrentLine(prev => prev + 1);
          setCurrentChar(0);
        }
      } else {
        setTypedCode('');
        setCurrentLine(0);
        setCurrentChar(0);
      }
    }, 40);
    return () => clearInterval(typeInterval);
  }, [currentLine, currentChar]);

  // ASCII morphing animation
  useEffect(() => {
    const asciiInterval = setInterval(() => {
      setAsciiFrame(prev => prev + 1);
    }, 80);
    return () => clearInterval(asciiInterval);
  }, []);

  // Generate morphing ASCII art
  const morphingAscii = useMemo(() => {
    const width = 24;
    const height = 8;
    const t = asciiFrame * 0.12;
    let result = '';

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const cx = width / 2, cy = height / 2;
        const dx = x - cx, dy = (y - cy) * 2;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        const wave1 = Math.sin(dist * 0.35 - t * 2);
        const wave2 = Math.cos(angle * 4 + t);
        const noise = Math.sin(x * 0.4 + t) * Math.cos(y * 0.7 - t * 0.5);

        const value = (wave1 * wave2 + noise * 0.5) * 0.5 + 0.5;
        const charIndex = Math.floor(value * (asciiChars.length - 1));
        result += asciiChars[Math.max(0, Math.min(charIndex, asciiChars.length - 1))];
      }
      result += '\n';
    }
    return result;
  }, [asciiFrame]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      maxWidth: '720px',
      margin: '0 auto',
      padding: `${spacing.xl} ${spacing.xl}`,
      boxSizing: 'border-box',
      minHeight: '100%',
    }}>
      {/* Greeting */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.md,
        marginBottom: spacing.xxl,
      }}>
        <img src={brandIcon} alt="" width={36} height={36} style={{ objectFit: 'contain' }} />
        <h1 style={{
          fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
          fontWeight: 400,
          color: colors.foreground,
          letterSpacing: '-0.02em',
          margin: 0,
          fontFamily: 'Georgia, "Times New Roman", serif',
        }}>
          {getGreeting()}
        </h1>
      </div>

      {/* Chat Container */}
      <div style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.lg,
      }}>
        {/* User Message (if we have a prompt) */}
        {userPrompt && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
          }}>
            <div style={{
              maxWidth: '80%',
              padding: `${spacing.md} ${spacing.lg}`,
              background: colors.foreground,
              color: colors.background,
              borderRadius: borderRadius.lg,
              borderBottomRightRadius: borderRadius.sm,
              fontSize: '0.9375rem',
              lineHeight: 1.5,
            }}>
              {userPrompt}
            </div>
          </div>
        )}

        {/* AI Response */}
        <div style={{
          display: 'flex',
          gap: spacing.md,
          alignItems: 'flex-start',
        }}>
          {/* AI Avatar */}
          <img
            src={brandIcon}
            alt="Significa"
            style={{
              width: 32,
              height: 32,
              borderRadius: borderRadius.md,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />

          {/* Response Content */}
          <div style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: spacing.sm,
          }}>
            {/* Status Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
            }}>
              <span style={{
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: colors.foreground,
              }}>
                Significa
              </span>
              <div className="thinking-dot" style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: '#6D916C',
              }} />
              <span style={{
                fontSize: '0.75rem',
                color: colors.foregroundSecondary,
              }}>
                {stages[currentStage]?.text}...
              </span>
            </div>

            {/* Generation Card */}
            <div style={{
              background: colors.backgroundPanel,
              borderRadius: borderRadius.lg,
              border: `1px solid ${colors.border}`,
              overflow: 'hidden',
            }}>
              {/* Progress Bar */}
              <div style={{
                height: 3,
                background: colors.backgroundOffset,
                position: 'relative',
                overflow: 'hidden',
              }}>
                <div style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '100%',
                  width: `${progress}%`,
                  background: '#6D916C',
                  transition: 'width 0.3s ease',
                }} />
              </div>

              {/* Content Grid */}
              <div className="gen-grid" style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                minHeight: '140px',
              }}>
                {/* Left: Code */}
                <div style={{
                  padding: spacing.md,
                  borderRight: `1px solid ${colors.border}`,
                  background: colors.background,
                }}>
                  <div style={{
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    color: colors.foregroundSecondary,
                    marginBottom: spacing.xs,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}>
                    Generating
                  </div>
                  <pre style={{
                    margin: 0,
                    fontSize: '0.6rem',
                    fontFamily: 'SF Mono, Monaco, Consolas, monospace',
                    color: colors.foreground,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}>
                    <span style={{ color: colors.foregroundSecondary }}>{'(x, y, t) => {\n'}</span>
                    {typedCode}
                    <span className="cursor">▋</span>
                  </pre>
                </div>

                {/* Right: Preview */}
                <div style={{
                  padding: spacing.md,
                  display: 'flex',
                  flexDirection: 'column',
                }}>
                  <div style={{
                    fontSize: '0.625rem',
                    fontWeight: 600,
                    color: colors.foregroundSecondary,
                    marginBottom: spacing.xs,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                  }}>
                    Preview
                  </div>
                  <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: colors.background,
                    borderRadius: borderRadius.sm,
                  }}>
                    <pre style={{
                      margin: 0,
                      fontSize: '0.5rem',
                      fontFamily: 'SF Mono, Monaco, Consolas, monospace',
                      color: colors.foreground,
                      lineHeight: 1.1,
                      letterSpacing: '0.04em',
                      opacity: 0.85,
                    }}>
                      {morphingAscii}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes pulse-stage {
          0%, 100% { box-shadow: 0 0 0 0 rgba(109, 145, 108, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(109, 145, 108, 0); }
        }
        .thinking-dot {
          animation: pulse-dot 1.5s ease-in-out infinite;
        }
        .cursor {
          animation: blink 0.8s step-end infinite;
          color: ${colors.foreground};
        }
        .stage-pending {
          background: ${colors.foregroundTertiary};
        }
        .stage-active {
          background: #6D916C;
          animation: pulse-stage 1.2s ease-in-out infinite;
        }
        .stage-done {
          background: #6D916C;
        }

        @media (max-width: 1024px) {
          .gen-grid {
            grid-template-columns: 1fr !important;
          }
          .gen-grid > div:first-child {
            border-right: none !important;
            border-bottom: 1px solid ${colors.border};
          }
          .gen-grid > div:last-child {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default GenerationSkeleton;
